import { timeFormat, timeParse } from 'd3-time-format';
import { line, curveMonotoneX, area } from 'd3-shape';
import { extent, max } from 'd3-array';
import { linearAxis, timeAxis } from '../../../factory/axes';

// Build a config object for a single small-multiple chart.
// `timeSeries` should be an array of objects with ISO dates (YYYY-MM-DD)
// and numeric cumulative fields on each row.
export default function buildConfig(props, timeSeries, metricConfig) {
  const { initialWidth, initialHeight, isMobile } = props;
  const {
    valueField, label, color, sharedMaxY,
  } = metricConfig;

  const margins = {
    top: 15,
    right: isMobile ? 30 : 40,
    bottom: 30,
    left: 30,
  };

  const width = initialWidth - margins.right - margins.left;
  const height = initialHeight - margins.top - margins.bottom;

  const parser = timeParse('%Y-%m-%d'); // incoming dates look like "2025-09-02"

  const values = (timeSeries || [])
    .map(d => ({
      date: parser(d.date),
      value: d[valueField] != null ? +d[valueField] : null,
    }))
    .filter(d => d.date && d.value != null);

  // Extend the line to yesterday with the last value
  if (values.length > 0) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const lastValue = values[values.length - 1].value;
    values.push({ date: yesterday, value: lastValue });
  }

  const data = [
    {
      id: label,
      values,
    },
  ];

  const maxValue = sharedMaxY != null ? sharedMaxY : (max(values, d => d.value) || 0);

  const xAxisConfig = {
    orientation: 'bottom',
    domain: extent(values, d => d.date),
    range: [0, width],
    nice: false,
  };

  const yAxisConfig = {
    orientation: 'left',
    domain: [0, maxValue],
    range: [height, 0],
    nice: false,
  };

  const { axis: xAxis, scale: x } = timeAxis(xAxisConfig);
  const { axis: yAxis, scale: y } = linearAxis(yAxisConfig);

  const path = line()
    .curve(curveMonotoneX)
    .x(d => x(d.date))
    .y(d => y(d.value));

  const areaPath = area()
    .curve(curveMonotoneX)
    .x(d => x(d.date))
    .y0(height)
    .y1(d => y(d.value));

  // color is a simple function so we can reuse the existing graphic code
  const colorScale = () => color;

  return {
    title: label,
    color: colorScale,
    data,
    fmtYearAbbrev: timeFormat('%y'),
    fmtYearFull: timeFormat('%Y'),
    fmtMonth: timeFormat('%B'),
    initialWidth,
    initialHeight,
    margins,
    height,
    path,
    areaPath,
    width,
    x,
    xAxis,
    y,
    yAxis,
  };
}
